/** Whether a sheet cell and the value a campaign already holds are the same
 *  value, however each is written ("3.06%" vs 3.06, "12k" vs 12000). TS port
 *  of discovr's internal/campaigns/import_review_rules.go rosterFieldAgrees
 *  and fieldValuesEqual — the server decides which rows need a Current/Sheet
 *  pick with these rules, so the review grid must draw a difference exactly
 *  where they find one. Keep the two in sync by hand. */
import type { ExpectedField, RosterMatch } from '@discovr/types';
import { parseAmount, sheetPrecisionUnit } from './amountParse';
import { USAGE_RIGHTS_DAYS_KEY, USERNAME_KEY, allowsFreeDeliverables, extractHandleClient, isBlankMetricCell } from './importWizardHelpers';

const ENGAGEMENT_KEY = 'engagement_rate';
const AVG_VIEWS_KEY = 'avg_views';
const FOLLOWERS_KEY = 'followers';
const REMOVE_TOKEN = 'remove';
const ENGAGEMENT_MIN_UNIT = 0.01;
const AVG_VIEWS_MIN_UNIT = 1;
const WHOLE_NUMBER_TOLERANCE = 1e-6;

/** Compared as rounded whole numbers: the import rounds them ("32.3k" is
 *  stored as 32300), so the stored count must still match the sheet's cell. */
const WHOLE_NUMBER_KEYS = new Set([FOLLOWERS_KEY, USAGE_RIGHTS_DAYS_KEY]);

function metricNumber(raw: string): number | null {
  return parseAmount(raw, 'metric');
}

function isWholeNumber(n: number): boolean {
  return Math.abs(n - Math.round(n)) <= WHOLE_NUMBER_TOLERANCE;
}

function wholeNumbersEqual(a: string, b: string): boolean {
  if (a === b) return true;
  const an = metricNumber(a);
  const bn = metricNumber(b);
  if (an === null || bn === null || !isWholeNumber(an) || !isWholeNumber(bn)) return false;
  return Math.round(an) === Math.round(bn);
}

function priceValuesEqual(a: string, b: string): boolean {
  if (a === b) return true;
  const an = parseAmount(a, 'price');
  const bn = parseAmount(b, 'price');
  return an !== null && bn !== null && an === bn;
}

/** "4.5" and "4.50" (or "10,000" and "10000") are one value; anything that is
 *  not a plain number must match exactly. */
function sheetValuesEqual(a: string, b: string): boolean {
  if (a === b) return true;
  const an = metricNumber(a);
  const bn = metricNumber(b);
  return an !== null && bn !== null && an === bn;
}

/** A stored engagement rate or average views against the sheet's cell at the
 *  precision the sheet gave it: the fetched 4.5123 and 48123.4 are the same as
 *  a sheet's 4.51 and 48123. Engagement is read to at least 2 decimals, views
 *  to a whole number, and "48.1k" is precise to 100. */
function metricsAgree(key: string, current: string, sheet: string): boolean {
  const cur = metricNumber(current);
  const sh = metricNumber(sheet);
  if (cur === null || sh === null) return false;
  const unit =
    key === ENGAGEMENT_KEY
      ? Math.min(sheetPrecisionUnit(sheet), ENGAGEMENT_MIN_UNIT)
      : Math.max(sheetPrecisionUnit(sheet), AVG_VIEWS_MIN_UNIT);
  return Math.round(cur / unit) === Math.round(sh / unit);
}

/** `writes` is false when the import leaves the field alone, whatever it
 *  holds; `agrees` only means something when it writes. */
type CellOutcome = { writes: false } | { writes: true; agrees: boolean };

const IGNORED: CellOutcome = { writes: false };

function outcome(agrees: boolean): CellOutcome {
  return { writes: true, agrees };
}

/** The deliverable-price cell the import acts on: a readable amount above 0
 *  (or exactly 0, unless the campaign is fixed fee) sets it, the
 *  word "remove" deletes it, and everything else — blank, wordy, negative —
 *  leaves it alone. Mirrors classifyDeliverableCell. */
function priceOutcome(has: boolean, current: string, sheet: string, payoutModel: string | undefined): CellOutcome {
  const trimmed = sheet.trim();
  if (trimmed === '') return IGNORED;
  if (trimmed.toLowerCase() === REMOVE_TOKEN) return has ? outcome(false) : IGNORED;
  const amount = parseAmount(sheet, 'price');
  if (amount === null || amount < 0) return IGNORED;
  if (amount === 0 && !allowsFreeDeliverables(payoutModel)) return IGNORED;
  return outcome(has && priceValuesEqual(current, sheet));
}

/** Engagement and average views: a blank, placeholder or 0 cell is "not
 *  supplied" and the stored figure is kept (parseCommitMetric). */
function metricOutcome(key: string, has: boolean, current: string, sheet: string): CellOutcome {
  if (isBlankMetricCell(sheet)) return IGNORED;
  const value = metricNumber(sheet);
  if (value === 0) return IGNORED;
  return outcome(has && value !== null && metricsAgree(key, current, sheet));
}

function followersOutcome(has: boolean, current: string, sheet: string): CellOutcome {
  if (isBlankMetricCell(sheet)) return IGNORED;
  const value = metricNumber(sheet);
  const readable = value !== null && isWholeNumber(value);
  if (readable && Math.round(value) === 0) return IGNORED;
  return outcome(has && readable && wholeNumbersEqual(current, sheet));
}

function cellOutcome(match: RosterMatch, field: ExpectedField, sheet: string, payoutModel: string | undefined): CellOutcome {
  const has = field.key in match.currentValues;
  const current = match.currentValues[field.key] ?? '';
  if (field.isDeliverable) return priceOutcome(has, current, sheet, payoutModel);
  if (field.key === ENGAGEMENT_KEY || field.key === AVG_VIEWS_KEY) {
    return match.statsFrozen === true ? IGNORED : metricOutcome(field.key, has, current, sheet);
  }
  if (field.key === FOLLOWERS_KEY) {
    return match.statsFrozen === true ? IGNORED : followersOutcome(has, current, sheet);
  }
  if (WHOLE_NUMBER_KEYS.has(field.key)) return outcome(has && wholeNumbersEqual(current, sheet));
  return outcome(has && sheetValuesEqual(current, sheet));
}

/** True when importing this sheet cell would change what the matched creator
 *  holds: the import acts on the cell (it is not blank, a placeholder or a
 *  frozen stat) and the cell reads differently from the stored value. */
export function sheetCellDiffers(
  match: RosterMatch,
  field: ExpectedField,
  sheet: string,
  payoutModel: string | undefined,
): boolean {
  const result = cellOutcome(match, field, sheet, payoutModel);
  return result.writes && !result.agrees;
}

/** Two rows' cells of one field, compared as the server does to tell duplicate
 *  rows that differ from ones that only write the same value differently. */
export function sheetCellsEqual(field: ExpectedField, a: string, b: string): boolean {
  if (field.key === USERNAME_KEY) return extractHandleClient(a, 'youtube') === extractHandleClient(b, 'youtube');
  if (field.isDeliverable) return priceValuesEqual(a, b);
  if (WHOLE_NUMBER_KEYS.has(field.key)) return wholeNumbersEqual(a, b);
  return sheetValuesEqual(a, b);
}
